import classNames from 'classnames';
import React, { useState } from 'react';
import TransactionFilter, { type CardOption } from '../../elements/transactions/TransactionFilter';
import TransactionTableList from '../../elements/transactions/TransactionTableList';

// Shared type for card type filter option
export interface CardTypeOption {
	value: 'smartcard' | 'creditcard' | 'bankcard';
	label: string;
}

type TransactionsTableProps = {
	wrapperClasses?: string;
	isCompany?: boolean;
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({ 
	wrapperClasses = 'white-pale-section shadow-one',
	isCompany = false
}) => {
	// Lifted state for card type filter - shared between TransactionFilter and TransactionTableList
	const [selectedCardType, setSelectedCardType] = useState<CardTypeOption | null>(null);
	// Lifted state for specific card filter
	const [selectedCard, setSelectedCard] = useState<CardOption | null>(null);
	// Lifted state for date filters - start with no dates selected (empty)
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	// Track if date filters have been explicitly set by user
	const [hasUserSetDates, setHasUserSetDates] = useState(false);

	// Helper function to auto-expand single date to 30-day range
	// Respects DatePicker constraints: start date maxDate = today, end date maxDate = today
	const getEffectiveDates = (start: Date | null, end: Date | null): { start: Date | null; end: Date | null } => {
		if (!start && !end) {
			return { start: null, end: null };
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0); // Set to start of today

		// If only start date is set, end date is 30 days after (but capped at today)
		if (start && !end) {
			const expandedEnd = new Date(start);
			expandedEnd.setDate(expandedEnd.getDate() + 30);
			// Cap at today since that's the max the DatePicker allows
			const cappedEnd = expandedEnd > today ? today : expandedEnd;
			return { start, end: cappedEnd };
		}

		// If only end date is set, start date is 30 days before
		if (!start && end) {
			const expandedStart = new Date(end);
			expandedStart.setDate(expandedStart.getDate() - 30);
			return { start: expandedStart, end };
		}

		// Both dates set - use as-is
		return { start, end };
	};

	const effectiveDates = getEffectiveDates(startDate, endDate);

	// Helper function to check if date span exceeds 2 years (API limitation)
	const isDateSpanExceedingTwoYears = (): boolean => {
		if (!startDate || !endDate) {
			return false;
		}

		// Calculate the difference in milliseconds
		const differenceMs = Math.abs(endDate.getTime() - startDate.getTime());
		
		// Convert to days (24 hours = 86400000 ms)
		const differenceDays = differenceMs / (1000 * 60 * 60 * 24);
		
		// 2 years = approximately 730 days (365 * 2), but we'll use 730 to be safe
		// This accounts for leap years: some 2-year periods have 731 days, some have 730
		const TWO_YEARS_IN_DAYS = 730;
		
		return differenceDays > TWO_YEARS_IN_DAYS;
	};

	return (
		<div
			className={classNames(
				'Row__Inner',
				'transactions-table',
				wrapperClasses
			)}
		>
			<div className="transactions">
				<div className="transactions-inner-wrapper">	
					<h2>Transaktioner</h2>
					<TransactionFilter 
						selectedCardType={selectedCardType}
						onCardTypeChange={setSelectedCardType}
						selectedCard={selectedCard}
						onCardChange={setSelectedCard}
						startDate={startDate}
						onStartDateChange={(date) => {
							setStartDate(date);
							setHasUserSetDates(true);
						}}
						endDate={endDate}
						onEndDateChange={(date) => {
							setEndDate(date);
							setHasUserSetDates(true);
						}}
						isCompany={isCompany}
						isDateSpanExceedingTwoYears={isDateSpanExceedingTwoYears()}
					/>

					<TransactionTableList 
						isCompany={isCompany} 
						selectedCardType={selectedCardType}
						selectedCard={selectedCard}
						startDate={hasUserSetDates ? effectiveDates.start : null}
						endDate={hasUserSetDates ? effectiveDates.end : null}
					/>
				</div>
			</div>
		</div>
	)
}

export default TransactionsTable
